import { useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { directUploadToStorage } from "@/lib/storageUpload";
import { useAuth } from "@/hooks/useAuth";
import { useSensitiveData } from "@/hooks/useSensitiveData";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { toast } from "sonner";
import { Loader2, Download, Camera, User, Cake, Eye, EyeOff } from "lucide-react";
import jsPDF from "jspdf";
import type { EmployeeMasterData, SalaryHistory, BonusHistory, BenefitsHistory, VehicleCosts, SpecialCertification } from "@/types/hr";
import { EmployeeArchiveActions } from "./EmployeeArchiveActions";
import { LicenseAndCertificationsForm } from "./LicenseAndCertificationsForm";

// Helper function to calculate age
const calculateAge = (birthDate: string): number => {
  const birth = new Date(birthDate);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
    age--;
  }
  return age;
};

interface Employee {
  id: string;
  name: string;
  position: string;
}

interface EmployeeMasterDataFormProps {
  employeeId: string;
  readOnly?: boolean;
}

interface Vehicle {
  id: string;
  name: string;
  manufacturer: string | null;
  license_plate: string | null;
  leasing_rate: number;
  insurance_monthly: number;
  tax_yearly: number;
}

interface VehicleOneTimeCost {
  id: string;
  vehicle_id: string;
  cost_type: string;
  amount: number;
  cost_date: string;
}

interface OtherCost {
  id: string;
  cost_type: string;
  amount: number;
  payment_frequency: string;
  date: string;
}

const DEFAULT_DEPARTMENTS = [
  "Geschäftsführung",
  "Verwaltung",
  "Produktion",
  "Montage",
  "Lager",
  "Vertrieb",
  "Kundenservice"
];

export function EmployeeMasterDataForm({ employeeId, readOnly = false }: EmployeeMasterDataFormProps) {
  const { user, userRole } = useAuth();
  const isAdmin = userRole === 'admin' || userRole === 'hr_manager';
  const isReadOnly = readOnly || userRole === 'employee';
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<EmployeeMasterData>({});
  const [managers, setManagers] = useState<Employee[]>([]);
  const [departments, setDepartments] = useState<string[]>(DEFAULT_DEPARTMENTS);
  const [employeeName, setEmployeeName] = useState("");
  const [employeePosition, setEmployeePosition] = useState("");
  const [employeeNumber, setEmployeeNumber] = useState("");
  const [salaryHistory, setSalaryHistory] = useState<SalaryHistory[]>([]);
  const [bonusHistory, setBonusHistory] = useState<BonusHistory[]>([]);
  const [benefitsHistory, setBenefitsHistory] = useState<BenefitsHistory[]>([]);
  const [vehicleCosts, setVehicleCosts] = useState<VehicleCosts[]>([]);
  const [profileImageUrl, setProfileImageUrl] = useState<string | null>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [archivedAt, setArchivedAt] = useState<string | null>(null);
  const [assignedVehicle, setAssignedVehicle] = useState<Vehicle | null>(null);
  const [vehicleOneTimeCosts, setVehicleOneTimeCosts] = useState<VehicleOneTimeCost[]>([]);
  const [otherCosts, setOtherCosts] = useState<OtherCost[]>([]);
  const [agSozialabgaben, setAgSozialabgaben] = useState<number>(0);
  const [excludeFromEvaluation, setExcludeFromEvaluation] = useState(false);
  const [showEvaluationDashboard, setShowEvaluationDashboard] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Sensitive data management
  const { 
    sensitiveData, 
    loading: sensitiveLoading, 
    loadSensitiveData, 
    saveSensitiveData,
    isAdmin: canEditSensitive 
  } = useSensitiveData(employeeId);
  const [sensitiveDataRevealed, setSensitiveDataRevealed] = useState(false);
  const [localSensitiveData, setLocalSensitiveData] = useState({
    tax_id: "",
    social_security_number: "",
    iban: "",
    bic: "",
    health_insurance_name: "",
    health_insurance_number: "",
  });

  useEffect(() => {
    // Load departments from localStorage
    const loadDepartments = () => {
      const stored = localStorage.getItem("departments");
      if (stored) {
        setDepartments(JSON.parse(stored));
      }
    };
    loadDepartments();
    
    // Listen for changes to departments
    const handleStorageSync = () => loadDepartments();
    window.addEventListener('storage-sync', handleStorageSync);
    return () => window.removeEventListener('storage-sync', handleStorageSync);
  }, []);

  useEffect(() => {
    if (user && employeeId) {
      loadData();
    }
  }, [user, employeeId]);
  
  // Sync sensitive data when revealed
  useEffect(() => {
    if (sensitiveDataRevealed && sensitiveData) {
      setLocalSensitiveData({
        tax_id: sensitiveData.tax_id || "",
        social_security_number: sensitiveData.social_security_number || "",
        iban: sensitiveData.iban || "",
        bic: sensitiveData.bic || "",
        health_insurance_name: sensitiveData.health_insurance_name || "",
        health_insurance_number: sensitiveData.health_insurance_number || "",
      });
    }
  }, [sensitiveData, sensitiveDataRevealed]);
  
  const handleRevealSensitiveData = async () => {
    if (!sensitiveDataRevealed) {
      await loadSensitiveData();
      setSensitiveDataRevealed(true);
    } else {
      setSensitiveDataRevealed(false);
    }
  };

  const loadData = async () => {
    try {
      const [employeeResult, managersResult, salaryResult, bonusResult, benefitsResult, vehicleResult, vehiclesResult, otherCostsResult] =
        await Promise.all([
          supabase.from("employees").select("*").eq("id", employeeId).single(),
          supabase.from("employees").select("id, name, position").order("name"),
          supabase
            .from("salary_history")
            .select("*")
            .eq("employee_id", employeeId)
            .order("effective_date", { ascending: false }),
          supabase
            .from("bonus_history")
            .select("*")
            .eq("employee_id", employeeId)
            .order("bonus_date", { ascending: false }),
          supabase
            .from("benefits_history")
            .select("*")
            .eq("employee_id", employeeId)
            .eq("status", "active")
            .is("end_date", null),
          supabase
            .from("vehicle_costs")
            .select("*")
            .eq("employee_id", employeeId)
            .order("date", { ascending: false }),
          // Load vehicle assigned to this employee
          supabase
            .from("vehicles")
            .select("id, name, manufacturer, license_plate, leasing_rate, insurance_monthly, tax_yearly, responsible_employee_id")
            .eq("responsible_employee_id", employeeId)
            .single(),
          // Load other costs (not vehicle-linked)
          supabase
            .from("vehicle_costs")
            .select("id, cost_type, amount, payment_frequency, date")
            .eq("employee_id", employeeId)
            .is("vehicle_id", null)
            .order("date", { ascending: false }),
        ]);

      if (employeeResult.error) throw employeeResult.error;
      if (managersResult.error) throw managersResult.error;

      const employee = employeeResult.data;
      setEmployeeName(employee.name);
      setEmployeePosition(employee.position);
      setEmployeeNumber(employee.employee_number || "");
      setProfileImageUrl(employee.profile_image_url);
      setArchivedAt(employee.archived_at);
      setExcludeFromEvaluation(employee.exclude_from_evaluation || false);
      setShowEvaluationDashboard(employee.show_evaluation_dashboard !== false); // Default true
      setFormData({
        birth_date: employee.birth_date,
        gender: employee.gender,
        nationality: employee.nationality,
        address_street: employee.address_street,
        address_city: employee.address_city,
        address_zip: employee.address_zip,
        address_country: employee.address_country,
        phone: employee.phone,
        mobile: employee.mobile,
        private_email: employee.private_email,
        business_email: employee.business_email,
        business_phone: employee.business_phone,
        emergency_contact_name: employee.emergency_contact_name,
        emergency_contact_phone: employee.emergency_contact_phone,
        // Sensitive fields are now loaded separately via useSensitiveData hook
        bank_name: employee.bank_name,
        contract_type: employee.contract_type,
        employment_start_date: employee.employment_start_date,
        employment_end_date: employee.employment_end_date,
        weekly_hours: employee.weekly_hours,
        monthly_average_hours: employee.monthly_average_hours,
        vacation_days: employee.vacation_days,
        department: employee.department,
        direct_manager_id: employee.direct_manager_id,
        notes: employee.notes,
        drivers_license_classes: Array.isArray(employee.drivers_license_classes) 
          ? employee.drivers_license_classes 
          : (typeof employee.drivers_license_classes === 'string' 
              ? JSON.parse(employee.drivers_license_classes) 
              : []),
        special_certifications: Array.isArray(employee.special_certifications) 
          ? employee.special_certifications 
          : (typeof employee.special_certifications === 'string' 
              ? JSON.parse(employee.special_certifications) 
              : []),
        security_clearance_valid_until: employee.security_clearance_valid_until,
      });

      setManagers(managersResult.data || []);
      setSalaryHistory(salaryResult.data || []);
      setBonusHistory(bonusResult.data || []);
      setBenefitsHistory(benefitsResult.data || []);
      setVehicleCosts(vehicleResult.data || []);
      setOtherCosts(otherCostsResult.data || []);
      
      // Set assigned vehicle if found
      if (vehiclesResult.data && !vehiclesResult.error) {
        setAssignedVehicle(vehiclesResult.data);
        
        // Load vehicle one-time costs
        const { data: oneTimeCostsData } = await supabase
          .from("vehicle_one_time_costs")
          .select("id, vehicle_id, cost_type, amount, cost_date")
          .eq("vehicle_id", vehiclesResult.data.id)
          .order("cost_date", { ascending: false });
        
        setVehicleOneTimeCosts(oneTimeCostsData || []);
      } else {
        setAssignedVehicle(null);
        setVehicleOneTimeCosts([]);
      }

      // Load latest AG Sozialabgaben from cost calculations
      const { data: costCalcData } = await supabase
        .from("employee_cost_calculations")
        .select("ag_pflichtkosten")
        .eq("employee_id", employeeId)
        .order("calculation_date", { ascending: false })
        .limit(1)
        .maybeSingle();
      
      setAgSozialabgaben(costCalcData?.ag_pflichtkosten || 0);
    } catch (error) {
      console.error("Error loading data:", error);
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Filter out undefined values and prepare clean data for update
      const cleanData: Record<string, any> = {
        name: employeeName,
        position: employeePosition,
        employee_number: employeeNumber || null,
      };
      Object.entries(formData).forEach(([key, value]) => {
        if (value !== undefined) {
          // Ensure JSONB fields are properly handled
          if (key === 'drivers_license_classes' || key === 'special_certifications') {
            cleanData[key] = Array.isArray(value) ? value : [];
          } else {
            cleanData[key] = value === "" ? null : value;
          }
        }
      });

      const { error } = await supabase
        .from("employees")
        .update(cleanData)
        .eq("id", employeeId);

      if (error) throw error;

      // Save sensitive data if admin and data was revealed/edited
      if (canEditSensitive && sensitiveDataRevealed) {
        const sensitiveSuccess = await saveSensitiveData({
          tax_id: localSensitiveData.tax_id || null,
          social_security_number: localSensitiveData.social_security_number || null,
          iban: localSensitiveData.iban || null,
          bic: localSensitiveData.bic || null,
          health_insurance_name: localSensitiveData.health_insurance_name || null,
          health_insurance_number: localSensitiveData.health_insurance_number || null,
        });
        
        if (!sensitiveSuccess) {
          toast.error("Fehler beim Speichern der verschlüsselten Daten");
          return;
        }
      }

      toast.success("Stammdaten erfolgreich gespeichert");
    } catch (error) {
      console.error("Error saving master data:", error);
      toast.error("Fehler beim Speichern der Stammdaten");
    } finally {
      setSaving(false);
    }
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast.error("Bitte wählen Sie eine Bilddatei aus");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Das Bild darf maximal 5MB groß sein");
      return;
    }

    setUploadingImage(true);
    try {
      const fileExt = file.name.split(".").pop();
      const fileName = `${employeeId}-${Date.now()}.${fileExt}`;
      const filePath = `${fileName}`;

      // Upload to storage (direct fetch to avoid problematic extra headers in some environments)
      await directUploadToStorage({
        bucket: "employee-profiles",
        path: filePath,
        file,
        cacheControl: "3600",
      });

      // Get public URL
      const { data: urlData } = supabase.storage
        .from("employee-profiles")
        .getPublicUrl(filePath);

      const publicUrl = urlData.publicUrl;

      // Update employee record
      const { error: updateError } = await supabase
        .from("employees")
        .update({ profile_image_url: publicUrl })
        .eq("id", employeeId);

      if (updateError) throw updateError;

      setProfileImageUrl(publicUrl);
      toast.success("Profilbild erfolgreich aktualisiert");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error(error instanceof Error ? error.message : "Fehler beim Hochladen des Bildes");
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // Berechnungen
  const getCurrentSalary = () => {
    if (salaryHistory.length === 0) return 0;
    return salaryHistory[0].base_salary;
  };

  const getTotalBonusThisYear = () => {
    const currentYear = new Date().getFullYear();
    return bonusHistory
      .filter((b) => new Date(b.bonus_date).getFullYear() === currentYear)
      .reduce((sum, b) => sum + b.amount, 0);
  };

  const calculateMonthlyBenefit = (benefit: BenefitsHistory) => {
    if (!benefit.monthly_value) return 0;
    const freq = benefit.payment_frequency || "monthly";
    if (freq === "yearly") return benefit.monthly_value / 12;
    if (freq === "half_yearly") return benefit.monthly_value / 6;
    if (freq === "quarterly") return benefit.monthly_value / 3;
    return benefit.monthly_value;
  };

  const getTotalMonthlyBenefits = () => {
    return benefitsHistory.reduce((sum, b) => sum + calculateMonthlyBenefit(b), 0);
  };

  // KFZ-Kosten Berechnung (basierend auf zugewiesenem Fahrzeug)
  const getVehicleLeasingMonthly = () => {
    return assignedVehicle?.leasing_rate || 0;
  };

  const getVehicleInsuranceMonthly = () => {
    return assignedVehicle?.insurance_monthly || 0;
  };

  const getVehicleTaxYearly = () => {
    return assignedVehicle?.tax_yearly || 0;
  };

  const getVehicleOneTimeCostsTotal = () => {
    const currentYear = new Date().getFullYear();
    return vehicleOneTimeCosts
      .filter((c) => new Date(c.cost_date).getFullYear() === currentYear)
      .reduce((sum, c) => sum + c.amount, 0);
  };

  // Kraftstoff/Energie-Kosten (Treibstoff + Strom) des aktuellen Jahres
  const getFuelEnergyCostsYearly = () => {
    const currentYear = new Date().getFullYear();
    return vehicleOneTimeCosts
      .filter((c) => (c.cost_type === "treibstoff" || c.cost_type === "strom") && new Date(c.cost_date).getFullYear() === currentYear)
      .reduce((sum, c) => sum + c.amount, 0);
  };

  const getFuelEnergyCostsMonthly = () => {
    const currentMonth = new Date().getMonth() + 1; // 1-12
    const yearlyTotal = getFuelEnergyCostsYearly();
    // Auf bisherige Monate umrechnen für realistischen Monatsdurchschnitt
    return currentMonth > 0 ? yearlyTotal / currentMonth : 0;
  };

  // Laufende KFZ-Kosten pro Monat (Leasing + Versicherung + Steuer/12)
  const getVehicleRecurringMonthly = () => {
    return getVehicleLeasingMonthly() + getVehicleInsuranceMonthly() + (getVehicleTaxYearly() / 12);
  };

  // Laufende KFZ-Kosten pro Jahr
  const getVehicleRecurringYearly = () => {
    return (getVehicleLeasingMonthly() * 12) + (getVehicleInsuranceMonthly() * 12) + getVehicleTaxYearly();
  };

  // Sonstige Kosten Berechnung
  const calculateOtherCostMonthly = (cost: OtherCost) => {
    const freq = cost.payment_frequency || "monthly";
    if (freq === "yearly") return cost.amount / 12;
    if (freq === "half_yearly") return cost.amount / 6;
    if (freq === "quarterly") return cost.amount / 3;
    if (freq === "one_time") return 0; // Einmalige Kosten nicht monatlich rechnen
    return cost.amount;
  };

  const calculateOtherCostYearly = (cost: OtherCost) => {
    const freq = cost.payment_frequency || "monthly";
    if (freq === "yearly") return cost.amount;
    if (freq === "half_yearly") return cost.amount * 2;
    if (freq === "quarterly") return cost.amount * 4;
    if (freq === "one_time") {
      // Nur einmalige Kosten des aktuellen Jahres
      const currentYear = new Date().getFullYear();
      return new Date(cost.date).getFullYear() === currentYear ? cost.amount : 0;
    }
    return cost.amount * 12;
  };

  const getTotalMonthlyOtherCosts = () => {
    return otherCosts.reduce((sum, c) => sum + calculateOtherCostMonthly(c), 0);
  };

  const getTotalYearlyOtherCosts = () => {
    return otherCosts.reduce((sum, c) => sum + calculateOtherCostYearly(c), 0);
  };

  const currentSalary = getCurrentSalary();
  const totalBonusThisYear = getTotalBonusThisYear();
  const monthlyBenefits = getTotalMonthlyBenefits();
  
  // KFZ-Kosten
  const vehicleRecurringMonthly = getVehicleRecurringMonthly();
  const vehicleRecurringYearly = getVehicleRecurringYearly();
  const vehicleOneTimeCostsYearly = getVehicleOneTimeCostsTotal();
  const totalVehicleCostsYearly = vehicleRecurringYearly + vehicleOneTimeCostsYearly;
  
  // Sonstige Kosten
  const monthlyOtherCosts = getTotalMonthlyOtherCosts();
  const yearlyOtherCosts = getTotalYearlyOtherCosts();

  // Kraftstoff/Energie
  const fuelEnergyCostsMonthly = getFuelEnergyCostsMonthly();
  const fuelEnergyCostsYearly = getFuelEnergyCostsYearly();

  // Gesamtkosten
  const totalMonthlyCosts = currentSalary + agSozialabgaben + monthlyBenefits + vehicleRecurringMonthly + fuelEnergyCostsMonthly + monthlyOtherCosts;
  const totalYearlyCosts = (currentSalary * 12) + (agSozialabgaben * 12) + totalBonusThisYear + (monthlyBenefits * 12) + totalVehicleCostsYearly + yearlyOtherCosts;

  const handleExportPDF = () => {
    const doc = new jsPDF();

    doc.setFontSize(18);
    doc.text("Mitarbeiter-Kostenübersicht", 20, 20);

    doc.setFontSize(12);
    doc.text(`Name: ${employeeName}`, 20, 35);
    doc.text(`Position: ${employeePosition}`, 20, 42);
    doc.text(`Datum: ${new Date().toLocaleDateString("de-DE")}`, 20, 49);

    doc.setFontSize(14);
    doc.text("Laufende monatliche Kosten", 20, 65);

    doc.setFontSize(10);
    let y = 75;
    doc.text(
      `Aktuelles Grundgehalt: ${currentSalary.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}`,
      25,
      y
    );
    y += 7;
    doc.text(
      `AG Sozialabgaben: ${agSozialabgaben.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}`,
      25,
      y
    );
    y += 7;
    doc.text(
      `Benefits (monatlich): ${monthlyBenefits.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}`,
      25,
      y
    );
    y += 7;
    doc.text(
      `KFZ-Kosten (monatlich): ${vehicleRecurringMonthly.toLocaleString("de-DE", {
        style: "currency",
        currency: "EUR",
      })}`,
      25,
      y
    );
    y += 7;
    doc.text(
      `Kraftstoff/Energie (Ø monatlich): ${fuelEnergyCostsMonthly.toLocaleString("de-DE", {
        style: "currency",
        currency: "EUR",
      })}`,
      25,
      y
    );
    y += 7;
    doc.text(
      `Sonstige Kosten (monatlich): ${monthlyOtherCosts.toLocaleString("de-DE", {
        style: "currency",
        currency: "EUR",
      })}`,
      25,
      y
    );
    y += 10;
    doc.setFontSize(12);
    doc.text(
      `Gesamt pro Monat: ${totalMonthlyCosts.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}`,
      25,
      y
    );

    y += 15;
    doc.setFontSize(14);
    doc.text("Jährliche Kostenübersicht", 20, y);
    y += 10;

    doc.setFontSize(10);
    doc.text(
      `Jahresgehalt: ${(currentSalary * 12).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}`,
      25,
      y
    );
    y += 7;
    doc.text(
      `AG Sozialabgaben (jährlich): ${(agSozialabgaben * 12).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}`,
      25,
      y
    );
    y += 7;
    doc.text(
      `Boni (bisher dieses Jahr): ${totalBonusThisYear.toLocaleString("de-DE", {
        style: "currency",
        currency: "EUR",
      })}`,
      25,
      y
    );
    y += 7;
    doc.text(
      `Benefits (jährlich): ${(monthlyBenefits * 12).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}`,
      25,
      y
    );
    y += 7;
    doc.text(
      `KFZ-Kosten (jährlich): ${totalVehicleCostsYearly.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}`,
      25,
      y
    );
    y += 7;
    doc.text(
      `Sonstige Kosten (jährlich): ${yearlyOtherCosts.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}`,
      25,
      y
    );
    y += 10;
    doc.setFontSize(12);
    doc.text(
      `Gesamt pro Jahr: ${totalYearlyCosts.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}`,
      25,
      y
    );

    if (benefitsHistory.length > 0) {
      y += 15;
      doc.setFontSize(14);
      doc.text("Aktive Benefits", 20, y);
      y += 8;
      doc.setFontSize(9);
      benefitsHistory.forEach((b) => {
        const monthlyVal = calculateMonthlyBenefit(b);
        doc.text(
          `- ${b.benefit_name}: ${monthlyVal.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}/Monat`,
          25,
          y
        );
        y += 5;
      });
    }

    doc.save(`${employeeName}_Kostenübersicht.pdf`);
    toast.success("PDF exportiert");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Mitarbeiter Stammdaten</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Gesamtkostenübersicht - nur für Admin und HR-Manager sichtbar */}
        {(userRole === 'admin' || userRole === 'hr_manager') && (
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10">
            <CardHeader className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-2 sm:space-y-0">
              <div>
                <CardTitle className="text-lg">Gesamtkostenübersicht</CardTitle>
                <CardDescription>Monatliche und jährliche Mitarbeiterkosten</CardDescription>
              </div>
              <Button onClick={handleExportPDF} size="sm" variant="outline" className="w-full sm:w-auto">
                <Download className="h-4 w-4 mr-2" />
                PDF Export
              </Button>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Monatliche Übersicht */}
              <div>
                <h3 className="text-base font-semibold mb-3">Laufende monatliche Kosten</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Grundgehalt</p>
                    <p className="text-lg font-bold">
                      {currentSalary.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">AG Sozialabgaben</p>
                    <p className="text-lg font-bold text-red-600">
                      {agSozialabgaben.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Benefits</p>
                    <p className="text-lg font-bold text-blue-600">
                      {monthlyBenefits.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">KFZ-Kosten</p>
                    <p className="text-lg font-bold text-orange-600">
                      {vehicleRecurringMonthly.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Kraftstoff/Energie</p>
                    <p className="text-lg font-bold text-amber-600">
                      {fuelEnergyCostsMonthly.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                    <p className="text-xs text-muted-foreground">Ø pro Monat (lfd. Jahr)</p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Sonstige Kosten</p>
                    <p className="text-lg font-bold text-purple-600">
                      {monthlyOtherCosts.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                  <div className="space-y-1 bg-background/50 p-2 rounded-lg">
                    <p className="text-xs text-muted-foreground font-semibold">Gesamt/Monat</p>
                    <p className="text-xl font-bold text-primary">
                      {totalMonthlyCosts.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                </div>
              </div>

              {/* Jährliche Übersicht */}
              <div className="pt-4 border-t">
                <h3 className="text-base font-semibold mb-3">Jährliche Kosten</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-8 gap-3">
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Jahresgehalt</p>
                    <p className="text-sm font-semibold">
                      {(currentSalary * 12).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">AG Sozialabgaben (jährlich)</p>
                    <p className="text-sm font-semibold text-red-600">
                      {(agSozialabgaben * 12).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Boni (dieses Jahr)</p>
                    <p className="text-sm font-semibold text-green-600">
                      {totalBonusThisYear.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Benefits (jährlich)</p>
                    <p className="text-sm font-semibold text-blue-600">
                      {(monthlyBenefits * 12).toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">KFZ (jährlich)</p>
                    <p className="text-sm font-semibold text-orange-600">
                      {totalVehicleCostsYearly.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                    {vehicleOneTimeCostsYearly > 0 && (
                      <p className="text-xs text-muted-foreground">inkl. {vehicleOneTimeCostsYearly.toLocaleString("de-DE", { style: "currency", currency: "EUR" })} einmalig</p>
                    )}
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Kraftstoff/Energie (jährlich)</p>
                    <p className="text-sm font-semibold text-amber-600">
                      {fuelEnergyCostsYearly.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground">Sonstige (jährlich)</p>
                    <p className="text-sm font-semibold text-purple-600">
                      {yearlyOtherCosts.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                  <div className="space-y-1 bg-background/50 p-2 rounded-lg">
                    <p className="text-xs text-muted-foreground font-semibold">Gesamt/Jahr</p>
                    <p className="text-xl font-bold text-primary">
                      {totalYearlyCosts.toLocaleString("de-DE", { style: "currency", currency: "EUR" })}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Rest des Formulars */}
        <fieldset disabled={isReadOnly} className="contents">
        <div className="grid gap-6 md:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Name & Position</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Profilbild */}
              <div className="flex items-center gap-4">
                <div className="relative group">
                  <Avatar className="h-20 w-20">
                    <AvatarImage src={profileImageUrl || undefined} alt={employeeName} />
                    <AvatarFallback className="text-xl">
                      <User className="h-8 w-8" />
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage}
                    className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
                  >
                    {uploadingImage ? (
                      <Loader2 className="h-6 w-6 text-white animate-spin" />
                    ) : (
                      <Camera className="h-6 w-6 text-white" />
                    )}
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
                <div className="flex-1 space-y-1">
                  <p className="text-sm text-muted-foreground">Profilbild</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploadingImage || isReadOnly}
                  >
                    {uploadingImage ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Hochladen...
                      </>
                    ) : (
                      <>
                        <Camera className="h-4 w-4 mr-2" />
                        Bild ändern
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Vorname</Label>
                  <Input
                    value={employeeName.split(" ")[0] || ""}
                    onChange={(e) => {
                      const lastName = employeeName.split(" ").slice(1).join(" ");
                      setEmployeeName(`${e.target.value} ${lastName}`.trim());
                    }}
                    placeholder="Vorname"
                    disabled={isReadOnly}
                  />
                </div>
                <div>
                  <Label>Nachname</Label>
                  <Input
                    value={employeeName.split(" ").slice(1).join(" ") || ""}
                    onChange={(e) => {
                      const firstName = employeeName.split(" ")[0] || "";
                      setEmployeeName(`${firstName} ${e.target.value}`.trim());
                    }}
                    placeholder="Nachname"
                    disabled={isReadOnly}
                  />
                </div>
              </div>
              <div>
                <Label>Position</Label>
                <Input
                  value={employeePosition}
                  onChange={(e) => setEmployeePosition(e.target.value)}
                  placeholder="Position"
                  disabled={isReadOnly}
                />
              </div>
              <div>
                <Label>Personalnummer</Label>
                <Input
                  value={employeeNumber}
                  onChange={(e) => setEmployeeNumber(e.target.value)}
                  placeholder="z.B. 001"
                  disabled={isReadOnly}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Persönliche Daten</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Geburtsdatum</Label>
                  <Input
                    type="date"
                    value={formData.birth_date || ""}
                    onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                    disabled={isReadOnly}
                  />
                </div>
                {formData.birth_date && (
                  <div>
                    <Label className="flex items-center gap-1">
                      <Cake className="h-4 w-4" />
                      Alter
                    </Label>
                    <div className="h-10 px-3 py-2 rounded-md border bg-muted flex items-center text-sm font-medium">
                      {calculateAge(formData.birth_date)} Jahre
                    </div>
                  </div>
                )}
              </div>
              <div>
                <Label>Geschlecht</Label>
                <Select
                  value={formData.gender || ""}
                  onValueChange={(value) => setFormData({ ...formData, gender: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="männlich">Männlich</SelectItem>
                    <SelectItem value="weiblich">Weiblich</SelectItem>
                    <SelectItem value="divers">Divers</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Nationalität</Label>
                <Input
                  value={formData.nationality || ""}
                  onChange={(e) => setFormData({ ...formData, nationality: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Adresse</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Straße</Label>
                <Input
                  value={formData.address_street || ""}
                  onChange={(e) => setFormData({ ...formData, address_street: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>PLZ</Label>
                  <Input
                    value={formData.address_zip || ""}
                    onChange={(e) => setFormData({ ...formData, address_zip: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Stadt</Label>
                  <Input
                    value={formData.address_city || ""}
                    onChange={(e) => setFormData({ ...formData, address_city: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Land</Label>
                <Input
                  value={formData.address_country || ""}
                  onChange={(e) => setFormData({ ...formData, address_country: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Kontaktdaten</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Telefon (privat)</Label>
                <Input
                  value={formData.phone || ""}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Mobil (privat)</Label>
                <Input
                  value={formData.mobile || ""}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                />
              </div>
              <div>
                <Label>Private E-Mail</Label>
                <Input
                  type="email"
                  value={formData.private_email || ""}
                  onChange={(e) => setFormData({ ...formData, private_email: e.target.value })}
                />
              </div>
              <div className="pt-4 border-t">
                <Label className="text-muted-foreground text-xs uppercase tracking-wide">Betriebliche Kontaktdaten</Label>
              </div>
              <div>
                <Label>Betriebliche Telefonnummer</Label>
                <Input
                  value={formData.business_phone || ""}
                  onChange={(e) => setFormData({ ...formData, business_phone: e.target.value })}
                  placeholder="z.B. +49 123 456789"
                />
              </div>
              <div>
                <Label>Betriebliche E-Mail</Label>
                <Input
                  type="email"
                  value={formData.business_email || ""}
                  onChange={(e) => setFormData({ ...formData, business_email: e.target.value })}
                  placeholder="z.B. max.mustermann@firma.de"
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Notfallkontakt</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Name</Label>
                <Input
                  value={formData.emergency_contact_name || ""}
                  onChange={(e) => setFormData({ ...formData, emergency_contact_name: e.target.value })}
                />
              </div>
              <div>
                <Label>Telefon</Label>
                <Input
                  value={formData.emergency_contact_phone || ""}
                  onChange={(e) => setFormData({ ...formData, emergency_contact_phone: e.target.value })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-base">Steuer & Sozialversicherung</CardTitle>
                <CardDescription className="text-xs text-muted-foreground">
                  🔒 Diese Daten sind verschlüsselt gespeichert und nur für Administratoren sichtbar.
                </CardDescription>
              </div>
              {canEditSensitive && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRevealSensitiveData}
                  disabled={sensitiveLoading}
                >
                  {sensitiveLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : sensitiveDataRevealed ? (
                    <>
                      <EyeOff className="h-4 w-4 mr-2" />
                      Ausblenden
                    </>
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Anzeigen
                    </>
                  )}
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              {!canEditSensitive ? (
                <p className="text-sm text-muted-foreground italic">
                  Diese Daten sind nur für Administratoren sichtbar.
                </p>
              ) : !sensitiveDataRevealed ? (
                <p className="text-sm text-muted-foreground italic">
                  Klicken Sie auf "Anzeigen", um die verschlüsselten Daten einzusehen.
                </p>
              ) : (
                <>
                  <div>
                    <Label>Steuer-ID</Label>
                    <Input
                      value={localSensitiveData.tax_id}
                      onChange={(e) => setLocalSensitiveData({ ...localSensitiveData, tax_id: e.target.value })}
                      placeholder="z.B. 12 345 678 901"
                    />
                  </div>
                  <div>
                    <Label>Sozialversicherungsnummer</Label>
                    <Input
                      value={localSensitiveData.social_security_number}
                      onChange={(e) => setLocalSensitiveData({ ...localSensitiveData, social_security_number: e.target.value })}
                      placeholder="z.B. 12 345678 A 123"
                    />
                  </div>
                  <div>
                    <Label>Krankenkasse</Label>
                    <Input
                      value={localSensitiveData.health_insurance_name}
                      onChange={(e) => setLocalSensitiveData({ ...localSensitiveData, health_insurance_name: e.target.value })}
                      placeholder="z.B. AOK, TK, Barmer"
                    />
                  </div>
                  <div>
                    <Label>Krankenkassennummer</Label>
                    <Input
                      value={localSensitiveData.health_insurance_number}
                      onChange={(e) => setLocalSensitiveData({ ...localSensitiveData, health_insurance_number: e.target.value })}
                      placeholder="Versichertennummer"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <div>
                <CardTitle className="text-base">Bankdaten</CardTitle>
                <CardDescription className="text-xs text-muted-foreground">
                  🔒 IBAN und BIC sind verschlüsselt gespeichert.
                </CardDescription>
              </div>
              {canEditSensitive && !sensitiveDataRevealed && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRevealSensitiveData}
                  disabled={sensitiveLoading}
                >
                  {sensitiveLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <Eye className="h-4 w-4 mr-2" />
                      Anzeigen
                    </>
                  )}
                </Button>
              )}
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Bank</Label>
                <Input
                  value={formData.bank_name || ""}
                  onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                  placeholder="z.B. Sparkasse, Volksbank"
                />
              </div>
              {!canEditSensitive ? (
                <p className="text-sm text-muted-foreground italic">
                  IBAN und BIC sind nur für Administratoren sichtbar.
                </p>
              ) : !sensitiveDataRevealed ? (
                <p className="text-sm text-muted-foreground italic">
                  Klicken Sie auf "Anzeigen", um IBAN und BIC einzusehen.
                </p>
              ) : (
                <>
                  <div>
                    <Label>IBAN</Label>
                    <Input
                      value={localSensitiveData.iban}
                      onChange={(e) => setLocalSensitiveData({ ...localSensitiveData, iban: e.target.value })}
                      placeholder="z.B. DE89 3704 0044 0532 0130 00"
                    />
                  </div>
                  <div>
                    <Label>BIC</Label>
                    <Input
                      value={localSensitiveData.bic}
                      onChange={(e) => setLocalSensitiveData({ ...localSensitiveData, bic: e.target.value })}
                      placeholder="z.B. COBADEFFXXX"
                    />
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Vertragsdaten</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Vertragsart</Label>
                <Select
                  value={formData.contract_type || ""}
                  onValueChange={(value) => setFormData({ ...formData, contract_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unbefristet">Unbefristet</SelectItem>
                    <SelectItem value="befristet">Befristet</SelectItem>
                    <SelectItem value="teilzeit">Teilzeit</SelectItem>
                    <SelectItem value="minijob">Minijob</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Eintrittsdatum</Label>
                <Input
                  type="date"
                  value={formData.employment_start_date || ""}
                  onChange={(e) => setFormData({ ...formData, employment_start_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Austrittsdatum</Label>
                <Input
                  type="date"
                  value={formData.employment_end_date || ""}
                  onChange={(e) => setFormData({ ...formData, employment_end_date: e.target.value })}
                />
              </div>
              <div>
                <Label>Wochenstunden</Label>
                <Input
                  type="number"
                  step="0.5"
                  value={formData.weekly_hours || ""}
                  onChange={(e) => setFormData({ ...formData, weekly_hours: parseFloat(e.target.value) })}
                />
              </div>
              <div>
                <Label>Monatliche Durchschnittsstunden</Label>
                <Input
                  type="number"
                  step="0.1"
                  value={formData.monthly_average_hours || ""}
                  onChange={(e) => setFormData({ ...formData, monthly_average_hours: parseFloat(e.target.value) })}
                  placeholder="Standard: 173.3"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Zur Berechnung des Stundenlohns aus dem Bruttolohn. Standard: 173,3 Std.
                </p>
              </div>
              <div>
                <Label>Urlaubstage</Label>
                <Input
                  type="number"
                  value={formData.vacation_days || ""}
                  onChange={(e) => setFormData({ ...formData, vacation_days: parseInt(e.target.value) })}
                />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Organisation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label>Abteilung</Label>
                <Select
                  value={formData.department || ""}
                  onValueChange={(value) => setFormData({ ...formData, department: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Abteilung auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {departments.map((dept) => (
                      <SelectItem key={dept} value={dept}>
                        {dept}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Direkter Vorgesetzter</Label>
                <Select
                  value={formData.direct_manager_id || ""}
                  onValueChange={(value) => setFormData({ ...formData, direct_manager_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Auswählen..." />
                  </SelectTrigger>
                  <SelectContent>
                    {managers
                      .filter((m) => m.id !== employeeId)
                      .map((manager) => (
                        <SelectItem key={manager.id} value={manager.id}>
                          {manager.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Admin-Only: Bewertungsausschluss */}
        {userRole === 'admin' && (
          <Card className="border-amber-200 bg-amber-50/50 dark:bg-amber-950/20 dark:border-amber-800">
            <CardHeader>
              <CardTitle className="text-base flex items-center gap-2">
                <span className="px-2 py-0.5 text-xs font-semibold bg-amber-500 text-white rounded">Admin</span>
                Bewertungseinstellungen
              </CardTitle>
              <CardDescription>
                Diese Einstellungen sind nur für Administratoren sichtbar.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label htmlFor="exclude-evaluation" className="text-sm font-medium">
                    Von Bewertung ausschließen
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Dieser Mitarbeiter erscheint nicht in der Bewertungsmatrix.
                  </p>
                </div>
                <Switch
                  id="exclude-evaluation"
                  checked={excludeFromEvaluation}
                  onCheckedChange={async (checked) => {
                    setExcludeFromEvaluation(checked);
                    // Direkt speichern
                    const { error } = await supabase
                      .from("employees")
                      .update({ exclude_from_evaluation: checked })
                      .eq("id", employeeId);
                    
                    if (error) {
                      toast.error("Fehler beim Speichern");
                      setExcludeFromEvaluation(!checked);
                    } else {
                      toast.success(checked 
                        ? "Mitarbeiter von Bewertung ausgeschlossen" 
                        : "Mitarbeiter wird wieder bewertet"
                      );
                    }
                  }}
                />
              </div>
              
              <div className="flex items-center justify-between border-t pt-4">
                <div className="space-y-0.5">
                  <Label htmlFor="show-dashboard" className="text-sm font-medium">
                    Bewertungs-Dashboard anzeigen
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Mitarbeiter sieht seinen Leistungsstand und Vergleich mit anderen.
                  </p>
                </div>
                <Switch
                  id="show-dashboard"
                  checked={showEvaluationDashboard}
                  onCheckedChange={async (checked) => {
                    setShowEvaluationDashboard(checked);
                    // Direkt speichern
                    const { error } = await supabase
                      .from("employees")
                      .update({ show_evaluation_dashboard: checked })
                      .eq("id", employeeId);
                    
                    if (error) {
                      toast.error("Fehler beim Speichern");
                      setShowEvaluationDashboard(!checked);
                    } else {
                      toast.success(checked 
                        ? "Dashboard für Mitarbeiter aktiviert" 
                        : "Dashboard für Mitarbeiter deaktiviert"
                      );
                    }
                  }}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {/* License and Certifications */}
        <LicenseAndCertificationsForm
          employeeId={employeeId}
          driversLicenseClasses={Array.isArray(formData.drivers_license_classes) ? formData.drivers_license_classes : []}
          specialCertifications={Array.isArray(formData.special_certifications) ? formData.special_certifications : []}
          securityClearanceValidUntil={formData.security_clearance_valid_until}
          onDriversLicenseChange={(classes) => setFormData({ ...formData, drivers_license_classes: classes })}
          onCertificationsChange={(certs) => setFormData({ ...formData, special_certifications: certs })}
          onSecurityClearanceChange={(date) => setFormData({ ...formData, security_clearance_valid_until: date || undefined })}
        />

        <div>
          <Label>Notizen</Label>
          <Textarea
            rows={4}
            value={formData.notes || ""}
            onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
            placeholder="Interne Notizen..."
          />
        </div>
        </fieldset>

        {!isReadOnly && (
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Speichern
          </Button>
        )}

        {/* Archive/Delete Actions - only for admins/hr_managers */}
        {!isReadOnly && (
          <EmployeeArchiveActions 
            employeeId={employeeId} 
            employeeName={employeeName}
            isArchived={!!archivedAt}
            onArchiveStatusChange={loadData}
          />
        )}
      </CardContent>
    </Card>
  );
}
